import React, { useState, useEffect, useCallback, useRef } from "react";

import { defaultCover, battleOfTheHunsId, christmasId, isMobile } from "../../../common/parameters";

import { coverHost } from "../../paths";

import TopBar from "./TopBar";
import QuickButtons from "./QuickButtons";
import Flags from "./Flags";
import LastRow from "./LastRow";

import { Card, LoaderTop, LoaderBottom, Gap } from "./styles";
import { IProps } from "./interfaces";
import { IPlan } from "../../../common/types";
import { useAfterHydration } from "../../../common/helpers/afterHydration";

const PlanCard = ({
    plan,
    index,
    tiny,
    browse,
    play,
    browseLoading,
    homework,
    homeworkResult,
    deleteHomework,
    classView,
    renameHomework,
    modifyHomeworkDate,
    modifyHomeworkDescription,
    showCreatedDate,
    handleArchivedChange,
    moderation,
    privateView,
    handleCheckPrivate,
    studentResults,
    homeworkTotalResults,
    homeworkLastResults,
    onComposerClick,
}: IProps) => {
    const imgAR = useRef<SVGSVGElement>(null);
    const card = useRef<HTMLDivElement>(null);
    const [hover, setHover] = useState(false);
    const [mobilScroll, setMobileScroll] = useState(false);
    const [imageHeight, setImageHeight] = useState<number | null>(null);
    const handleScroll = useCallback(() => {
        if (!card.current) return;
        const scrollTop = (document.scrollingElement || document.documentElement).scrollTop;
        const height = window.innerHeight;
        const cardHeight = card.current.getBoundingClientRect().height;
        if (card.current.offsetTop - scrollTop > -30 && card.current.offsetTop - scrollTop + cardHeight + 30 < height) {
            setMobileScroll(true);
        } else {
            setMobileScroll(false);
        }
    }, [setMobileScroll, card]);
    // isMobile comes from the user agent, which only the browser has - so it cannot take
    // part in the first render, which has to match the markup the server sent.
    const afterHydration = useAfterHydration();
    const handleResize = useCallback(() => {
        if (imgAR.current) {
            setImageHeight(imgAR.current.getBoundingClientRect().width * (720 / 1280));
        }
    }, [setImageHeight, imgAR]);
    useEffect(() => {
        if (isMobile) {
            window.addEventListener("scroll", handleScroll);
        }
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    return (
        <Card
            ref={card}
            tiny={tiny}
            play={!!play}
            disabled={browseLoading}
            onMouseMove={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            extra={battleOfTheHunsId === plan._id || christmasId === plan._id}
            christmas={christmasId === plan._id}
        >
            {!tiny &&
                (!browseLoading ? (
                    <TopBar
                        plan={plan as IPlan}
                        isBrowse={browse}
                        index={index}
                        isPlay={!!play}
                        isHomework={homework}
                        isHomeworkResult={homeworkResult}
                        deleteHomework={deleteHomework}
                        renameHomework={renameHomework}
                        modifyHomeworkDate={modifyHomeworkDate}
                        modifyHomeworkDescription={modifyHomeworkDescription}
                        isModeration={moderation}
                    />
                ) : (
                    <LoaderTop />
                ))}
            <div
                className={"Image"}
                style={{
                    background: browseLoading ? `#CCCCCC` : "",
                    backgroundImage: plan.cover
                        ? `url(${coverHost}${plan.cover}), url(${coverHost}${defaultCover}-en.jpg)`
                        : browseLoading
                            ? ""
                            : `url(${coverHost}${defaultCover}-en.jpg)`,
                }}
            >
                <svg
                    width="100%"
                    viewBox="0 0 128 72"
                    ref={imgAR}
                    style={{ height: imageHeight || "auto" }}
                />
                {!play && (
                    <QuickButtons
                        isMobile={afterHydration && isMobile}
                        tiny={tiny}
                        hover={hover}
                        mobilScroll={mobilScroll}
                        classView={classView}
                        browse={browse}
                        plan={plan as IPlan}
                        homework={!!homework}
                        homeworkResult={homeworkResult}
                        moderation={moderation}
                        studentResults={studentResults}
                        privateView={privateView}
                    />
                )}
                {christmasId !== plan._id && (
                    <Flags
                        plan={plan as IPlan}
                        browse={!!browse}
                        homeworkTotalResults={homeworkTotalResults}
                        homeworkLastResults={homeworkLastResults}
                    />
                )}
            </div>
            {browseLoading ? (
                <LoaderBottom>
                    <Gap></Gap>
                </LoaderBottom>
            ) : (
                <LastRow
                    plan={plan as IPlan}
                    tiny={tiny}
                    isBrowse={browse}
                    play={play}
                    isHomework={!!homework}
                    showCreatedDate={!!showCreatedDate}
                    handleArchivedChange={handleArchivedChange}
                    moderation={moderation}
                    privateView={privateView}
                    handleCheckPrivate={handleCheckPrivate}
                    onComposerClick={onComposerClick}
                />
            )}
        </Card>
    );
}

export default PlanCard;
